/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import { action } from "@ember/object";
import Component from "@glimmer/component";
import move from "ember-animated/motions/move";
import { fadeIn, fadeOut } from "ember-animated/motions/opacity";
import type { BufferedChangeset } from "ember-changeset/types";

export interface ModelListItem {
  item: unknown;
  index: number;

  destroy: () => void;

  canMoveUp: boolean;
  moveUp: () => void;

  canMoveDown: boolean;
  moveDown: () => void;
}

interface CommonArgs {
  createItem: () => unknown;
  onDestroyItem: (index: number) => void;

  listClass: string;
  addButtonTitle?: string;
}

interface ChangesetBasedModelList extends CommonArgs {
  model: BufferedChangeset;
  propertyName: string;
}

interface ListBasedModelList extends CommonArgs {
  items: Array<unknown>;
  onUpdateItems: (items: Array<unknown>) => void;
}

export type ModelListArgs = ChangesetBasedModelList | ListBasedModelList;

export interface ModelListComponentSignature {
  Args: ModelListArgs;
  Blocks: {
    default: [ModelListItem];
  };
}

/**
 * This component helps handle list of things in a component.
 * This helper provides the logic for handling lists:
 * - adding an item,
 * - deleting one,
 * - moving items (coming later).
 *
 * The model is usually a changeset.
 */
export default class ModelListComponent<
  ActualModelListSignature extends ModelListComponentSignature
> extends Component<ActualModelListSignature> {
  private get isChangesetBased(): boolean {
    return Boolean((this.args as unknown as ChangesetBasedModelList).model);
  }

  private get changesetBasedArgs(): ChangesetBasedModelList {
    return this.args as unknown as ChangesetBasedModelList;
  }

  private get listBasedArgs(): ListBasedModelList {
    return this.args as unknown as ListBasedModelList;
  }

  get items(): ListBasedModelList["items"] {
    if (this.isChangesetBased) {
      return this.changesetBasedArgs.model.get(
        this.changesetBasedArgs.propertyName
      );
    }
    return this.listBasedArgs.items;
  }

  get lastItemIndex(): number {
    return Math.max(0, this.items.length - 1);
  }

  @action
  addItem() {
    this.addItemObject(this._createItem());
  }

  addItemObject(obj: unknown) {
    this._updateList(list => {
      list.push(obj);
    });
  }

  @action
  destroyItem(index: number) {
    if (this.args.onDestroyItem) {
      this.args.onDestroyItem(index);
    }
    this._updateList(list => {
      list.splice(index, 1);
    });
  }

  @action
  moveItemUp(index) {
    if (index == 0) return;

    this._updateList(items => {
      const item = items[index];
      items.splice(index, 1);
      items.splice(index - 1, 0, item);
    });
  }

  @action
  moveItemDown(index) {
    if (index == this.items.length - 1) return;

    this._updateList(items => {
      const item = items[index];
      items.splice(index, 1);
      items.splice(index + 1, 0, item);
    });
  }

  *transition({ insertedSprites, keptSprites, removedSprites }) {
    for (const sprite of insertedSprites) {
      fadeIn(sprite);
    }
    for (const sprite of keptSprites) {
      move(sprite);
    }
    for (const sprite of removedSprites) {
      fadeOut(sprite);
    }
    yield;
  }

  /**
   * Calls the given constructor if it exists
   * or create an empty POJO.
   */
  _createItem() {
    return this.args.createItem ? this.args.createItem() : {};
  }

  _updateList(operation) {
    if (this.isChangesetBased) {
      const items = this.changesetBasedArgs.model
        .get(this.changesetBasedArgs.propertyName)
        .slice(0);
      operation(items);
      this.changesetBasedArgs.model.set(
        this.changesetBasedArgs.propertyName,
        items
      );
    } else {
      const items = this.listBasedArgs.items.slice();
      operation(items);
      this.listBasedArgs.onUpdateItems(items);
    }
  }
}
